import React, { PropsWithChildren, useContext } from 'react'
import { Link as RouterLink } from 'react-router-dom'

// Components
import { DrawerItem } from './DrawerItem'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { NavigatorContext, NavigatorTheme } from './NavigatorContext'
import clsx from 'clsx'

// Utils
import { concatStyles } from 'utils'

const HREF_STYLES =
  'flex flex-row flex-grow py-2 px-3 min-w-0 font-heading font-bold text-base no-underline transition-colors'

interface HrefProps {
  href?: string
  onClick?: (event: React.MouseEvent<Element, MouseEvent>) => void
  active?: boolean
  appearanceTheme: NavigatorTheme
}

export const Href: React.FC<PropsWithChildren<HrefProps>> = ({
  appearanceTheme,
  active,
  children,
  href,
  onClick: handleOnClick,
}) => {
  const isSecondary: boolean = appearanceTheme === 'secondary'

  const HREF_BACKGROUND_STYLES = active
    ? isSecondary
      ? 'bg-navbackgroundactivealt'
      : 'bg-navbackgroundactive'
    : 'bg-transparent'

  const HREF_COLOR_STYLES = active
    ? 'text-white'
    : isSecondary
    ? 'text-navcontentalt'
    : 'text-white'

  const HREF_HOVER_STYLES = isSecondary
    ? 'hover:bg-navbackgroundactivealt hover:text-white'
    : 'hover:bg-navbackgroundactive hover:text-white'

  return (
    <a
      href={href}
      onClick={handleOnClick}
      className={clsx(
        HREF_STYLES,
        HREF_BACKGROUND_STYLES,
        HREF_COLOR_STYLES,
        HREF_HOVER_STYLES
      )}
    >
      {children}
    </a>
  )
}

Href.displayName = 'Navigator.Href'

const SUB_HREF_STYLES =
  'flex flex flex-grow py-2 px-3 min-w-0 font-body text-sm no-underline transition-colors'

interface SubHrefProps extends HrefProps {
  selected?: boolean
}

export const SubHref: React.FC<PropsWithChildren<SubHrefProps>> = ({
  appearanceTheme,
  active,
  children,
  href,
  onClick: handleOnClick,
  selected,
}) => {
  const isSecondary = appearanceTheme === 'secondary'

  const SUB_HREF_BACKGROUND_STYLES = active
    ? isSecondary
      ? 'bg-navbackgroundactivealt'
      : 'bg-navbackgroundactive'
    : 'bg-transparent'

  const SUB_HREF_COLOR_STYLES = active
    ? 'text-white'
    : isSecondary
    ? 'text-navcontentalt'
    : 'text-white'

  const SUB_HREF_HOVER_STYLES = isSecondary
    ? 'hover:bg-navbackgroundactivealt hover:text-white'
    : 'hover:bg-navbackgroundactive hover:text-white'

  return (
    <a
      href={href}
      onClick={handleOnClick}
      className={concatStyles(
        SUB_HREF_STYLES,
        SUB_HREF_BACKGROUND_STYLES,
        SUB_HREF_COLOR_STYLES,
        SUB_HREF_HOVER_STYLES,
        selected ? 'font-semibold' : 'font-light'
      )}
    >
      {children}
    </a>
  )
}

SubHref.displayName = 'Navigator.SubHref'

const LABEL_STYLES =
  'flex flex-row my-auto mr-3 ml-0 xl:flex font-heading font-bold tracking-tight text-xl'

interface ILabelProps {
  compact?: boolean
  style?: React.CSSProperties
}

export const Label: React.FC<PropsWithChildren<ILabelProps>> = ({
  compact,
  style = {},
  children,
}) => {
  return (
    <span
      className={concatStyles(LABEL_STYLES, compact ? 'md:hidden' : '')}
      style={style}
    >
      {children}
    </span>
  )
}

Label.displayName = 'Navigator.Label'

const LINK_STYLES =
  'flex flex-row flex-grow py-2 px-3 h-16 min-w-0 text-base font-heading font-bold no-underline transition-colors'
interface LinkProps {
  to: string
  active?: boolean
  renderCompact?: boolean
  appearanceTheme: NavigatorTheme
}

export const Link: React.FC<PropsWithChildren<LinkProps>> = ({
  children,
  to,
  active,
  renderCompact,
  appearanceTheme,
}) => {
  const isSecondary = appearanceTheme === 'secondary'

  const LINK_BACKGROUND_STYLES = active
    ? isSecondary
      ? 'bg-navbackgroundactivealt'
      : 'bg-navbackgroundactive'
    : isSecondary
    ? 'bg-transparent'
    : 'bg-transparent'

  const LINK_HOVER_STYLES = isSecondary
    ? 'hover:bg-navbackgroundactivealt hover:text-white'
    : 'hover:bg-navbackgroundactive hover:text-white'

  const LINK_COLOR_STYLES = active
    ? 'text-white'
    : isSecondary
    ? 'text-navcontentalt'
    : 'text-white'

  return (
    <RouterLink
      to={to}
      className={concatStyles(
        LINK_STYLES,
        LINK_BACKGROUND_STYLES,
        LINK_COLOR_STYLES,
        LINK_HOVER_STYLES
      )}
    >
      {children}
    </RouterLink>
  )
}

Link.displayName = 'Navigator.Link'

export interface ItemProps {
  active?: boolean
  to: string
  label: string
  icon?: IconDefinition
  labelStyle?: React.CSSProperties
}

/**
 * An item for the primary navigation drawer.
 * @param props Configuration for the item.
 */
export const Item: React.FC<ItemProps> = ({
  active,
  to,
  label,
  labelStyle,
  icon,
}) => {
  const { compact, theme } = useContext(NavigatorContext)
  const iconColorClass =
    theme === 'secondary'
      ? active
        ? 'text-actionable'
        : 'text-navcontentalt'
      : active
      ? 'text-actionable'
      : 'text-primary'

  return (
    <DrawerItem appearanceTheme={theme} active={active}>
      <Link
        appearanceTheme={theme}
        active={active}
        renderCompact={compact}
        to={to}
      >
        {icon ? (
          <FontAwesomeIcon
            icon={icon}
            size="1x"
            className={concatStyles(['my-auto ml-3 mr-4', iconColorClass])}
          />
        ) : null}
        {compact ? null : <Label style={labelStyle}>{label}</Label>}
      </Link>
    </DrawerItem>
  )
}

Item.displayName = 'Navigator.Item'
